import { apiClient, ApiError } from './client';
import type {
  CreateSecretRequest,
  CreateSecretResponse,
  EmailSecretResponse,
  RetrieveSecretResponse,
  SecretListItem,
} from '@/types/oscar';

/**
 * Typed wrappers over the OnPass API (oscar/api/Functions/SecretsFunctions.cs,
 * PR-22).
 *
 * Auth model: create / list / expire require admin or helpdesk; retrieve only
 * requires an authenticated principal (no Oscar role) — the high-entropy token
 * is the primary control. See oscar/docs/OnPass-Plan.md.
 */

/** POST /api/secrets — create a one-time link (admin / helpdesk). */
export function createSecret(body: CreateSecretRequest): Promise<CreateSecretResponse> {
  return apiClient.post<CreateSecretResponse>('/api/secrets', body);
}

/** GET /api/secrets — the caller's own active pushes (admin / helpdesk). */
export function listSecrets(signal?: AbortSignal): Promise<SecretListItem[]> {
  return apiClient.get<SecretListItem[]>('/api/secrets', signal);
}

/** POST /api/secrets/:token/expire — destroy now (admin / helpdesk; idempotent). */
export function expireSecret(token: string): Promise<{ Token: string; Destroyed: boolean }> {
  return apiClient.post<{ Token: string; Destroyed: boolean }>(
    `/api/secrets/${encodeURIComponent(token)}/expire`,
  );
}

/**
 * POST /api/secrets/:token/email — email the one-time link to a chosen Entra
 * member (OnPass audience). The server builds the canonical link and
 * re-validates the recipient (enabled member) before sending; the plaintext is
 * never included. `recipientId` is the Entra object id from {@link searchUsers}.
 */
export function emailSecret(token: string, recipientId: string, message?: string): Promise<EmailSecretResponse> {
  return apiClient.post<EmailSecretResponse>(
    `/api/secrets/${encodeURIComponent(token)}/email`,
    { RecipientId: recipientId, Message: message?.trim() ? message : undefined },
  );
}

/**
 * GET /api/secrets/:token — retrieve and reveal once (any authenticated user).
 *
 * The optional passphrase is sent in the `X-OnPass-Passphrase` header (never
 * the query string — that would be logged). A 401 from this authenticated
 * route means "a correct passphrase is required" (the SWA guarantees the caller
 * is signed in); 404 means the secret is no longer available.
 *
 * Implemented as a direct fetch (rather than apiClient.get) so we can attach
 * the passphrase header and surface the 401/404 status to the caller.
 */
export async function retrieveSecret(
  token: string,
  passphrase?: string,
  signal?: AbortSignal,
): Promise<RetrieveSecretResponse> {
  let response: Response;
  try {
    response = await fetch(`/api/secrets/${encodeURIComponent(token)}`, {
      method: 'GET',
      headers: passphrase ? { 'X-OnPass-Passphrase': passphrase } : undefined,
      credentials: 'include',
      signal,
    });
  } catch (err) {
    throw new ApiError(err instanceof Error ? err.message : 'Network error', 0, 'network');
  }

  if (response.status === 401 || response.status === 403) {
    // 401 = a correct passphrase is required. 403 = the secret is locked to a
    // different account; surface the server's specific message.
    let message =
      response.status === 401
        ? 'A correct passphrase is required to view this secret.'
        : 'This secret is locked to a different Onsite account.';
    if (response.status === 403) {
      try {
        const data = (await response.json()) as { error?: string };
        if (data?.error) message = data.error;
      } catch {
        // non-JSON body — keep the default locked message
      }
    }
    throw new ApiError(message, response.status, 'auth');
  }

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const data = (await response.json()) as { error?: string };
      if (data?.error) message = data.error;
    } catch {
      // non-JSON body — keep the generic message
    }
    throw new ApiError(message, response.status, 'http');
  }

  return (await response.json()) as RetrieveSecretResponse;
}
